import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { fetchAllAdminData } from '../../store/admin';
import { fetchMoves } from '../../store/moves';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [activeTab, setActiveTab] = useState('trailers');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [timeRange, setTimeRange] = useState('daily');
  const searchQuery = useSelector((state) => state.ui.searchQuery);

  const companies = useSelector((state) => Array.isArray(state.companies) ? state.companies : Object.values(state.companies));
  const accounts = useSelector((state) => Array.isArray(state.accounts) ? state.accounts : Object.values(state.accounts));
  const trucks = useSelector((state) => Array.isArray(state.trucks) ? state.trucks : Object.values(state.trucks));
  const trailers = useSelector((state) => Array.isArray(state.trailers) ? state.trailers : Object.values(state.trailers));
  const buildings = useSelector((state) => Array.isArray(state.buildings) ? state.buildings : Object.values(state.buildings));
  const yards = useSelector((state) => Array.isArray(state.yards) ? state.yards : Object.values(state.yards));
  const users = useSelector((state) => Array.isArray(state.users) ? state.users : Object.values(state.users));
  const moves = useSelector((state) => state.moves?.list || []);

  useEffect(() => {
    dispatch(fetchAllAdminData());
    dispatch(fetchMoves());
  }, [dispatch]);

  useEffect(() => {
    if (companies.length && !selectedCompanyId) {
      const firstCompany = companies[0];
      setSelectedCompanyId(firstCompany.id);

      const firstCompanyAccount = accounts.find(account => account.company_id === firstCompany.id);
      if (firstCompanyAccount) {
        setSelectedAccountId(firstCompanyAccount.id);
      }
    }
  }, [companies, accounts, selectedCompanyId]);

  useEffect(() => {
    if (selectedCompanyId) {
      const companyAccounts = accounts.filter(account => account.company_id === selectedCompanyId);
      if (companyAccounts.length > 0) {
        setSelectedAccountId(companyAccounts[0].id);
      }
    }
  }, [selectedCompanyId, accounts]);

  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down 300px
      setShowScrollButton(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Function to get status priority for sorting
  const getStatusPriority = (item) => {
    if (item.red_tagged) return 1; // Highest priority
    if (item.yellow_tagged) return 2;
    if (item.in_shop) return 3;
    return 4; // Available - lowest priority
  };

  // Add this function to filter moves based on time range
  const getFilteredMoves = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return moves.filter(move => {
      const moveDate = new Date(move.request_time);

      switch (timeRange) {
        case 'daily':
          return moveDate.toDateString() === today.toDateString();
        case 'weekly': {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return moveDate >= sevenDaysAgo;
        }
        case 'monthly': {
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return moveDate >= thirtyDaysAgo;
        }
        default:
          return true;
      }
    });
  };

  // Add this function to calculate average completion time
  const calculateAverageTime = (filteredMoves) => {
    const completedMoves = filteredMoves.filter(move => move.finish_time);
    if (completedMoves.length === 0) return 'N/A';

    const totalMinutes = completedMoves.reduce((acc, move) => {
      const start = new Date(move.request_time);
      const end = new Date(move.finish_time);
      return acc + (end - start) / (1000 * 60);
    }, 0);

    return Math.round(totalMinutes / completedMoves.length) + ' min';
  };

  if (!companies.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // const selectedCompany = companies.find(c => c.id === selectedCompanyId);
  const companyAccounts = accounts.filter(account => account.company_id === selectedCompanyId);

  // Filter trucks by account
  let accountTrucks = trucks.filter(truck => truck.account_id === selectedAccountId);

  // Apply search filter to trucks
  accountTrucks = accountTrucks.filter((truck) => {
    const query = searchQuery?.trim();
    if (!query) return true;
    const lowerQuery = query.toLowerCase();

    return (
      (truck.truck_location?.toLowerCase()?.includes(lowerQuery) || false) ||
      (truck.truck_number?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
      (truck.type?.toLowerCase()?.includes(lowerQuery) || false) ||
      (truck.make?.toLowerCase()?.includes(lowerQuery) || false) ||
      (truck.model?.toLowerCase()?.includes(lowerQuery) || false) ||
      (truck.license_plate?.toLowerCase()?.includes(lowerQuery) || false) ||
      (truck.vin?.toLowerCase()?.includes(lowerQuery) || false)
    );
  });

  // Sort trucks if sortField is set
  if (sortField) {
    accountTrucks = [...accountTrucks].sort((a, b) => {
      // Special handling for status sorting
      if (sortField === 'status') {
        const statusA = getStatusPriority(a);
        const statusB = getStatusPriority(b);

        return sortDirection === 'asc'
          ? statusA - statusB
          : statusB - statusA;
      }

      // Regular field sorting
      let compareA = a[sortField] || '';
      let compareB = b[sortField] || '';

      if (sortDirection === 'asc') {
        return compareA.toString().localeCompare(compareB.toString());
      } else {
        return compareB.toString().localeCompare(compareA.toString());
      }
    });
  }

  // Filter and apply search to trailers
  let accountTrailers = trailers.filter(trailer => trailer.account_id === selectedAccountId);
  accountTrailers = accountTrailers.filter((trailer) => {
    const query = searchQuery?.trim();
    if (!query) return true;
    const lowerQuery = query.toLowerCase();

    // Safely determine load status
    let loadStatus = "empty";
    try {
      if (trailer.is_tray && trailer.tray_count) {
        loadStatus = String(trailer.tray_count);
      } else if (trailer.is_loaded && trailer.load_number) {
        loadStatus = String(trailer.load_number);
      }
    } catch (error) {
      console.error('Error determining load status:', error);
    }

    // Safely check all fields
    return (
      (trailer.trailer_location?.toLowerCase()?.includes(lowerQuery) || false) ||
      (trailer.trailer_number?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
      (trailer.type?.toLowerCase()?.includes(lowerQuery) || false) ||
      (trailer.license_plate?.toLowerCase()?.includes(lowerQuery) || false) ||
      (trailer.company_owner?.toLowerCase()?.includes(lowerQuery) || false) ||
      (trailer.load_number?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
      loadStatus.toLowerCase().includes(lowerQuery)
    );
  });

  // Sort trailers if sortField is set
  if (sortField) {
    accountTrailers = [...accountTrailers].sort((a, b) => {
      // Special handling for status sorting
      if (sortField === 'status') {
        const statusA = getStatusPriority(a);
        const statusB = getStatusPriority(b);

        return sortDirection === 'asc'
          ? statusA - statusB
          : statusB - statusA;
      }

      // Regular field sorting
      let compareA = a[sortField] || '';
      let compareB = b[sortField] || '';

      if (sortDirection === 'asc') {
        return compareA.toString().localeCompare(compareB.toString());
      } else {
        return compareB.toString().localeCompare(compareA.toString());
      }
    });
  }

  // Filter and apply search to yards
  let accountYards = yards.filter(yard => yard.account_id === selectedAccountId);
  accountYards = accountYards.filter((yard) => {
    const query = searchQuery?.trim();
    if (!query) return true;
    const lowerQuery = query.toLowerCase();

    return (
      (yard.name?.toLowerCase()?.includes(lowerQuery) || false) ||
      (yard.address?.toLowerCase()?.includes(lowerQuery) || false) ||
      (yard.city?.toLowerCase()?.includes(lowerQuery) || false) ||
      (yard.state?.toLowerCase()?.includes(lowerQuery) || false) ||
      (yard.zip_code?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
      (yard.description?.toLowerCase()?.includes(lowerQuery) || false)
    );
  });

  // Sort yards if sortField is set
  if (sortField) {
    accountYards = [...accountYards].sort((a, b) => {
      let compareA = a[sortField] || '';
      let compareB = b[sortField] || '';

      if (sortDirection === 'asc') {
        return compareA.toString().localeCompare(compareB.toString());
      } else {
        return compareB.toString().localeCompare(compareA.toString());
      }
    });
  }

  // Filter and apply search to buildings
  let accountBuildings = buildings.filter(building => building.account_id === selectedAccountId);
  accountBuildings = accountBuildings.filter((building) => {
    const query = searchQuery?.trim();
    if (!query) return true;
    const lowerQuery = query.toLowerCase();

    return (
      (building.name?.toLowerCase()?.includes(lowerQuery) || false) ||
      (building.address?.toLowerCase()?.includes(lowerQuery) || false) ||
      (building.city?.toLowerCase()?.includes(lowerQuery) || false) ||
      (building.state?.toLowerCase()?.includes(lowerQuery) || false) ||
      (building.zip_code?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
      (building.description?.toLowerCase()?.includes(lowerQuery) || false)
    );
  });

  // Sort buildings if sortField is set
  if (sortField) {
    accountBuildings = [...accountBuildings].sort((a, b) => {
      let compareA = a[sortField] || '';
      let compareB = b[sortField] || '';

      if (sortDirection === 'asc') {
        return compareA.toString().localeCompare(compareB.toString());
      } else {
        return compareB.toString().localeCompare(compareA.toString());
      }
    });
  }

  // Filter and apply search to users
  let accountUsers = users.filter(user => user.account_id === selectedAccountId || user.account_id === null);
  accountUsers = accountUsers.filter((user) => {
    const query = searchQuery?.trim();
    if (!query) return true;
    const lowerQuery = query.toLowerCase();

    return (
      (user.alpha_code?.toLowerCase()?.includes(lowerQuery) || false) ||
      (user.email?.toLowerCase()?.includes(lowerQuery) || false) ||
      (user.driver_number?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
      (user.is_active ? 'active' : 'inactive').includes(lowerQuery)
    );
  });

  // Sort users if sortField is set
  if (sortField) {
    accountUsers = [...accountUsers].sort((a, b) => {
      // Special handling for status sorting
      if (sortField === 'is_active') {
        return sortDirection === 'asc'
          ? (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1)
          : (a.is_active === b.is_active ? 0 : a.is_active ? 1 : -1);
      }

      // Regular field sorting
      let compareA = a[sortField] || '';
      let compareB = b[sortField] || '';

      if (sortDirection === 'asc') {
        return compareA.toString().localeCompare(compareB.toString());
      } else {
        return compareB.toString().localeCompare(compareA.toString());
      }
    });
  }

  // Add user statistics
  const userStats = {
    total: accountUsers.length,
    active: accountUsers.filter(user => user.is_active).length,
    inactive: accountUsers.filter(user => !user.is_active).length
  };

  const getStatusColor = (item) => {
    if (item.red_tagged) return 'bg-red-100 text-red-800';
    if (item.yellow_tagged) return 'bg-yellow-100 text-yellow-800';
    if (item.in_shop) return 'bg-blue-100 text-blue-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = (item) => {
    if (item.red_tagged) return 'Red Tagged';
    if (item.yellow_tagged) return 'Yellow Tagged';
    if (item.in_shop) return 'In Shop';
    return 'Available';
  };

  // SortableHeader component
  const SortableHeader = ({ field, label }) => (
    <div
      onClick={() => handleSort(field)}
      className="flex items-center justify-center cursor-pointer hover:bg-blue-200 p-2 rounded"
    >
      {label}
      {sortField === field && (
        <span className="ml-1">
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </div>
  );

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="text-5xl font-semibold mb-6 bg-gradient-to-r from-blue-800 via-red-700 to-orange-500 text-transparent bg-clip-text">
        Admin Dashboard
      </div>

      {/* Moves Summary Section */}
      <div className="relative bg-white rounded-lg shadow p-8 mb-8 z-10">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-4xl font-bold">Moves Summary</h2>
          <div className="flex gap-4">
            <label className={`inline-flex items-center cursor-pointer px-4 py-2 rounded-lg ${
              timeRange === 'daily' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}>
              <input
                type="radio"
                className="hidden"
                value="daily"
                checked={timeRange === 'daily'}
                onChange={(e) => setTimeRange(e.target.value)}
              />
              <span className="text-lg font-medium">Today</span>
            </label>
            <label className={`inline-flex items-center cursor-pointer px-4 py-2 rounded-lg ${
              timeRange === 'weekly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}>
              <input
                type="radio"
                className="hidden"
                value="weekly"
                checked={timeRange === 'weekly'}
                onChange={(e) => setTimeRange(e.target.value)}
              />
              <span className="text-lg font-medium">Past 7 Days</span>
            </label>
            <label className={`inline-flex items-center cursor-pointer px-4 py-2 rounded-lg ${
              timeRange === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}>
              <input
                type="radio"
                className="hidden"
                value="monthly"
                checked={timeRange === 'monthly'}
                onChange={(e) => setTimeRange(e.target.value)}
              />
              <span className="text-lg font-medium">Past 30 Days</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 min-h-[8rem]">
          {/* Get filtered moves once for all tiles */}
          {(() => {
            const filteredMoves = getFilteredMoves();
            const completedMoves = filteredMoves.filter(move => move.finish_time && !move.is_cancel);
            const successRate = filteredMoves.length > 0
              ? Math.round((completedMoves.length / filteredMoves.length) * 100)
              : 0;

            return (
              <>
                <div className="flex flex-col bg-blue-500 rounded-lg p-6 text-white shadow-lg min-h-[8rem] relative z-10">
                  <h3 className="text-2xl font-semibold mb-2">Total Moves</h3>
                  <p className="text-4xl font-bold">{filteredMoves.length}</p>
                </div>
                <div className="flex flex-col bg-green-500 rounded-lg p-6 text-white shadow-lg min-h-[8rem] relative z-10">
                  <h3 className="text-2xl font-semibold mb-2">Average Time</h3>
                  <p className="text-4xl font-bold">{calculateAverageTime(filteredMoves)}</p>
                </div>
                <div className="flex flex-col bg-yellow-500 rounded-lg p-6 text-white shadow-lg min-h-[8rem] relative z-10">
                  <h3 className="text-2xl font-semibold mb-2">Completed Moves</h3>
                  <p className="text-4xl font-bold">{completedMoves.length}</p>
                </div>
                <div className="flex flex-col bg-purple-500 rounded-lg p-6 text-white shadow-lg min-h-[8rem] relative z-10">
                  <h3 className="text-2xl font-semibold mb-2">Success Rate</h3>
                  <p className="text-4xl font-bold">{successRate}%</p>
                </div>
              </>
            );
          })()}
        </div>

        {/* Detailed Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Move Types Distribution */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-2xl font-semibold mb-4">Move Types</h3>
            <div className="space-y-4">
              {['single', 'shuttle', 'triangle'].map(type => {
                const filteredMoves = getFilteredMoves();
                const count = filteredMoves.filter(move => move.move_details?.type === type).length;
                const percentage = filteredMoves.length > 0 ? (count / filteredMoves.length) * 100 : 0;
                return (
                  <div key={type} className="flex items-center">
                    <span className="text-xl capitalize w-24">{type}</span>
                    <div className="flex-1 mx-4 bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-blue-600 rounded-full h-4"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-xl font-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Move Status Distribution */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-2xl font-semibold mb-4">Move Status</h3>
            <div className="space-y-4">
              {[
                { label: 'Requested', filter: move => !move.in_progress && !move.finish_time && !move.is_cancel },
                { label: 'In Progress', filter: move => move.in_progress && !move.finish_time && !move.is_cancel },
                { label: 'Completed', filter: move => move.finish_time && !move.is_cancel },
                { label: 'Cancelled', filter: move => move.is_cancel }
              ].map(({ label, filter }) => {
                const filteredMoves = getFilteredMoves();
                const count = filteredMoves.filter(filter).length;
                const percentage = filteredMoves.length > 0 ? (count / filteredMoves.length) * 100 : 0;
                return (
                  <div key={label} className="flex items-center">
                    <span className="text-xl w-32">{label}</span>
                    <div className="flex-1 mx-4 bg-gray-200 rounded-full h-4">
                      <div
                        className={`rounded-full h-4 ${
                          label === 'Completed' ? 'bg-green-600' :
                          label === 'In Progress' ? 'bg-yellow-600' :
                          label === 'Cancelled' ? 'bg-red-600' :
                          'bg-blue-600'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-xl font-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Company Selector */}
      <div className="w-1/2 mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <label className="text-4xl font-bold">Company:</label>
          <select
            value={selectedCompanyId || ''}
            onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
            className="flex-1 p-4 border border-gray-300 rounded-lg shadow-sm text-3xl"
          >
            {companies.map(company => (
              <option key={company.id} value={company.id}>
                {company.company_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCompanyId && (
        <div className="grid grid-cols-1 gap-8">
          {/* Accounts List */}
          <div className="bg-white rounded-lg shadow p-8">
            <h2 className="text-4xl font-bold mb-6">Accounts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {companyAccounts.map(account => (
                <div
                  key={account.id}
                  onClick={() => setSelectedAccountId(account.id)}
                  className={`border-2 rounded-lg p-6 cursor-pointer transition-all duration-200 ${
                    selectedAccountId === account.id
                      ? 'border-blue-500 shadow-xl bg-blue-100'
                      : 'border-gray-200 hover:shadow-xl hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]'
                  }`}
                >
                  <h3 className="text-3xl font-semibold mb-3">{account.account_name}</h3>
                  <p className="text-2xl text-gray-600">{account.location}</p>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <QuickStat
                      label="Trucks"
                      value={trucks.filter(t => t.account_id === account.id).length}
                      color="blue"
                    />
                    <QuickStat
                      label="Trailers"
                      value={trailers.filter(t => t.account_id === account.id).length}
                      color="green"
                    />
                    <QuickStat
                      label="Users"
                      value={users.filter(u => u.account_id === account.id).length}
                      color="purple"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Account Details */}
          {selectedAccountId && (
            <div className="bg-white rounded-lg shadow p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-4xl font-bold text-blue-800">
                  {accounts.find(a => a.id === selectedAccountId)?.account_name}
                </h2>
                <div className="flex gap-4">
                  {['trailers', 'trucks', 'yards', 'buildings', 'users'].map(tab => (
                    <button id={tab}
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-6 py-3 rounded-lg text-2xl font-semibold transform transition-all duration-200 ${
                        activeTab === tab
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-md hover:scale-110'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content based on activeTab */}

              {/* Trailers Tab */}
              {activeTab === 'trailers' && (
                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-3xl font-semibold">Trailers Overview</h3>
                    <div className="flex gap-4">
                      <button id='add-trailer-button'
                        onClick={() => history.push(`/trailers/new`)}
                        className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <span className="mr-2">+</span> Add New Trailer
                      </button>
                      <button id='bulk-upload-trailers-button'
                        onClick={() => history.push(`/trailers/bulk-upload`)}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <span className="mr-2">↑</span> Bulk Upload Trailers
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1.5fr] bg-blue-100 border-b border-gray-300 p-4 text-2xl font-semibold">
                    <div className="flex items-center justify-center">Location</div>
                    <div className="flex items-center justify-center">Trailer</div>
                    <div className="flex items-center justify-center">Type</div>
                    <div className="flex items-center justify-center">State - Plate</div>
                    <div className="flex items-center justify-center">Trailer Number</div>
                    <div className="flex items-center justify-center">Owner</div>
                    <div className="flex items-center justify-center">Load Number</div>
                    <div className="flex items-center justify-center">Tag Description</div>
                  </div>
                  {accountTrailers.map((trailer, index) => (
                    <div id='trailer-row'
                      className={`grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1.5fr] border-b border-gray-200 p-4 text-center
                        ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        hover:shadow-xl hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                        transition-all duration-200 cursor-pointer ${trailer?.red_tagged ? 'text-red-600' : ''}`}
                      key={trailer?.id}
                      onClick={() => history.push(`/trailers/${trailer?.id}`)}
                    >
                      <div className="flex items-center justify-center text-xl font-semibold">{trailer?.trailer_location || "Unknown"}</div>
                      <div className="flex items-center justify-center">
                        <img
                          className="max-h-16"
                          src={
                            trailer?.is_loaded
                              ? "/images/trailer-loadedYard.jpeg"
                              : trailer?.is_tray
                              ? "/images/trailer-traysFull.jpeg"
                              : "/images/trailer-empty.jpeg"
                          }
                          alt={
                            trailer?.is_loaded
                              ? "Loaded Trailer"
                              : trailer?.is_tray
                              ? "Trailer with Trays"
                              : "Empty Trailer"
                          }
                        />
                      </div>
                      <div className="flex items-center justify-center text-xl font-semibold">{trailer?.type || "N/A"}</div>
                      <div className="flex items-center justify-center text-xl font-semibold">{`${trailer?.license_state || ""} - ${trailer?.license_plate || ""}`}</div>
                      <div className="flex items-center justify-center text-xl font-semibold">{trailer?.trailer_number || "N/A"}</div>
                      <div className="flex items-center justify-center text-xl font-semibold">{trailer?.company_owner || "Unknown"}</div>
                      <div className="flex items-center justify-center text-xl font-semibold">
                        {trailer?.is_tray
                          ? trailer?.tray_count
                          : trailer?.is_loaded
                          ? trailer?.load_number
                          : "Empty"}
                      </div>
                      <div className="flex items-center justify-center min-w-[150px]">
                        <span
                          className={`px-4 py-2 rounded-md ${getStatusColor(trailer)}
                            break-words text-center w-full max-h-20 overflow-y-auto text-xl font-semibold`}
                        >
                          {trailer?.tagged_description || ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Trucks Tab */}
              {activeTab === 'trucks' && (
                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-3xl font-bold">Trucks Overview</h3>
                    <button id='add-truck-button'
                      onClick={() => history.push(`/trucks/new`)}
                      className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <span className="mr-2">+</span> Add New Truck
                    </button>
                  </div>
                  <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1.5fr_1.5fr] bg-blue-100 border-b border-gray-300 p-4 text-2xl font-semibold">
                    <SortableHeader field="truck_location" label="Location" />
                    <div className="flex items-center justify-center">Truck</div>
                    <SortableHeader field="truck_number" label="Number" />
                    <SortableHeader field="type" label="Type" />
                    <SortableHeader field="make" label="Make" />
                    <SortableHeader field="model" label="Model" />
                    <SortableHeader field="assigned_user_id" label="Assigned To" />
                    <SortableHeader field="status" label="Status" />
                  </div>
                  {accountTrucks.map((truck, index) => {
                    const assignedUser = users.find(u => u.id === truck?.assigned_user_id);
                    return (
                      <div id='truck-row'
                        className={`grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1.5fr_1.5fr] border-b border-gray-200 p-4 text-center
                          ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          hover:shadow-xl hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                          transition-all duration-200 cursor-pointer ${truck?.red_tagged ? 'text-red-600' : ''}`}
                        key={truck?.id}
                        onClick={() => history.push(`/trucks/${truck?.id}`)}
                      >
                        <div id='truck_location' className="flex items-center justify-center text-xl font-semibold">{truck?.truck_location || "Unknown"}</div>
                        <div className="flex items-center justify-center">
                          <img
                            className="max-h-16"
                            src="/images/truck.png"
                            alt="Truck"
                          />
                        </div>
                        <div id='truck_number'className="flex items-center justify-center text-xl font-semibold">{truck?.truck_number || "N/A"}</div>
                        <div id='type' className="flex items-center justify-center text-xl font-semibold">{truck?.type || "N/A"}</div>
                        <div id='make' className="flex items-center justify-center text-xl font-semibold">{truck?.make || "N/A"}</div>
                        <div id='model'className="flex items-center justify-center text-xl font-semibold">{truck?.model || "N/A"}</div>
                        <div id='alpha_code' className="flex items-center justify-center text-xl font-semibold">{assignedUser ? `${assignedUser.alpha_code}` : 'Not assigned'}</div>
                        <div id='status' className="flex items-center justify-center">
                          <span className={`px-4 py-2 rounded-md ${getStatusColor(truck)} text-xl font-semibold`}>
                            {getStatusText(truck)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Yards Tab */}
              {activeTab === 'yards' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-3xl font-semibold">Yards Overview</h3>
                    <div className="flex gap-4">
                      <button id='add-yard-button'
                        onClick={() => history.push(`/yards/new`)}
                        className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <span className="mr-2">+</span> Add New Yard
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {accountYards.map(yard => (
                      <div id='yard-row'
                        key={yard.id}
                        onClick={() => history.push(`/yards/${yard.id}`)}
                        className="border-2 border-gray-200 rounded-lg p-6 hover:shadow-xl
                          hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                          transition-all duration-200 cursor-pointer"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <h4 className="text-3xl font-semibold">{yard.name}</h4>
                          <span className={`px-4 py-2 rounded-full text-xl ${
                            yard.parking_spots ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {yard.parking_spots ? 'Parking Spots Available' : 'No Parking Spots'}
                          </span>
                        </div>
                        <div className="space-y-3 text-2xl">
                          <p><span className="font-medium">Location:</span> {yard.parking_location}</p>
                          {yard.entry_log && (
                            <p className="text-gray-600">
                              Last Entry: {new Date(yard.entry_log.entry_time).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Buildings Tab */}
              {activeTab === 'buildings' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-3xl font-semibold">Buildings Overview</h3>
                    <button id='add-building-button'
                      onClick={() => history.push(`/buildings/new`)}
                      className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <span className="mr-2">+</span> Add New Building
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {accountBuildings.map(building => (
                      <div id='building-row'
                        key={building.id}
                        onClick={() => history.push(`/buildings/${building.id}`)}
                        className="border-2 border-gray-200 rounded-lg p-6 hover:shadow-xl
                          hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                          transition-all duration-200 cursor-pointer"
                      >
                        <div className="text-2xl">
                          <div className="mt-4">
                            <span className="font-medium">Building ID:</span>
                            <p className="text-3xl mt-1">{building.id}</p>
                          </div>
                          <div className="mt-4">
                            <span className="font-medium">Building Name:</span>
                            <p className="text-3xl mt-1">{building.building_name}</p>
                          </div>
                        </div>

                        <div className="text-2xl">
                          <div className="mt-4">
                            <span className="font-medium">Dock Doors:</span>
                            <p className="text-3xl mt-1">{building.building_door_number}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex gap-4">
                      <QuickStat
                        label="Total Users"
                        value={userStats.total}
                        color="bg-blue-100 text-blue-800"
                      />
                      <QuickStat
                        label="Active Users"
                        value={userStats.active}
                        color="bg-green-100 text-green-800"
                      />
                      <QuickStat
                        label="Inactive Users"
                        value={userStats.inactive}
                        color="bg-red-100 text-red-800"
                      />
                    </div>
                    <button
                      id='add-user-button'
                      onClick={() => history.push('/signup')}
                      className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <span className="mr-2">+</span> Add New User
                    </button>
                  </div>
                  <div className="loadTrailer">
                    <div className="loadTrailer_header grid-cols-[1fr_2fr_1fr_1fr_1fr_1.5fr]">
                      <SortableHeader field="alpha_code" label="Alpha Code" />
                      <SortableHeader field="email" label="Email" />
                      <SortableHeader field="role" label="Role" />
                      <SortableHeader field="is_active" label="Status" />
                      <SortableHeader field="created_at" label="Created" />
                      <SortableHeader field="updated_at" label="Last Updated" />
                    </div>
                    {accountUsers.map((user) => (
                      <div
                        key={user.id}
                        className={`grid-row grid-cols-[1fr_2fr_1fr_1fr_1fr_1.5fr] border-2 border-gray-200 hover:shadow-xl
                          hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                          transition-all duration-200 cursor-pointer ${!user.is_active ? 'opacity-75' : ''}`}
                        onClick={() => history.push(`/users/${user.id}`)}
                      >
                        <div>{user.alpha_code || "N/A"}</div>
                        <div>{user.email || "N/A"}</div>
                        <div>
                          {user.is_super_manager ? "Super Manager" :
                           user.is_account_manager ? "Account Manager" :
                           user.is_manager ? "Manager" :
                           user.is_admin ? "Admin" :
                           user.is_security ? "Security" :
                           user.is_mechanic ? "Mechanic" :
                           user.is_road_driver ? "Road Driver" :
                           user.is_yard_driver ? "Yard Driver" :
                           "User"}
                        </div>
                        <div>
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            user.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Deactivated'}
                          </span>
                        </div>
                        <div>{user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</div>
                        <div>{user.updated_at ? new Date(user.updated_at).toLocaleDateString() : "N/A"}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {showScrollButton && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-200 z-50"
          aria-label="Scroll to top"
        >
          <svg
            className="w-12 h-12"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

const QuickStat = ({ label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    purple: 'bg-purple-100 text-purple-800'
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow">
      <div className="text-xl text-gray-600">{label}</div>
      <div className={`text-4xl font-bold mt-2 ${colorClasses[color]?.split(' ')[1]}`}>
        {value}
      </div>
    </div>
  );
};

export default AdminDashboard;
